/** Raw events remain separate. Classification always uses the full original interval. */
export const SHORT_PRODUCTION_DOWNTIME_MS = 60_000;
export type DowntimeInterval = { startedAt: string; endedAt: string | null };

export function completedDowntimeDurationMs(downtime: DowntimeInterval): number | null {
  if (!downtime.endedAt) return null;
  const start = Date.parse(downtime.startedAt), end = Date.parse(downtime.endedAt);
  return Number.isFinite(start) && Number.isFinite(end) && end >= start ? end - start : null;
}
export function isShortProductionDowntime(downtime: DowntimeInterval): boolean {
  const duration = completedDowntimeDurationMs(downtime);
  return duration !== null && duration < SHORT_PRODUCTION_DOWNTIME_MS;
}
export function completedDowntimeRequiresReason(downtime: DowntimeInterval): boolean {
  // Malformed completed legacy data is never silently exempted.
  return downtime.endedAt !== null && !isShortProductionDowntime(downtime);
}
export function openDowntimeRequiresReason(downtime: DowntimeInterval, now: number): boolean {
  if (downtime.endedAt !== null) return false;
  const start = Date.parse(downtime.startedAt);
  return Number.isFinite(start) && Number.isFinite(now) && now - start >= SHORT_PRODUCTION_DOWNTIME_MS;
}
/** First reason entry has no age limit; malformed legacy dates still require review. */
export function downtimeReasonNeedsApproval(endedAt: string | null): boolean {
  if (endedAt === null) return false;
  const end = Date.parse(endedAt);
  return !Number.isFinite(end);
}
export function downtimeReportSummary(
  downtimes: readonly DowntimeInterval[], now: number,
  window?: { start: number; end: number },
) {
  let totalMilliseconds = 0, shortStopMilliseconds = 0, shortStopCount = 0, invalidIntervalCount = 0;
  for (const event of downtimes) {
    const start = Date.parse(event.startedAt), end = event.endedAt === null ? now : Date.parse(event.endedAt);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) { invalidIntervalCount += 1; continue; }
    const from = window ? Math.max(start, window.start) : start;
    const to = window ? Math.min(end, window.end) : end;
    const inside = window ? start < window.end && end > window.start : true;
    if (!inside || to < from) continue;
    const duration = Math.max(0, to - from);
    totalMilliseconds += duration;
    if (isShortProductionDowntime(event)) { shortStopCount += 1; shortStopMilliseconds += duration; }
  }
  return { totalMilliseconds, shortStopMilliseconds, shortStopCount, invalidIntervalCount };
}
